/**
 * ComputeTraceStore — durable, append-only persistence for the COMPUTE-AXIS
 * graded-trace corpus of the HoloScript native-model program (P.004 / P.005 /
 * EXP-3).
 *
 * WHY THIS EXISTS
 * ───────────────
 * scripts/corpus/harvest_real.py computes the honest headline: there is
 * essentially NO real graded data for the exact-value COMPUTE task — the task
 * EXP-1c proved a base 1.5B model cannot do (0% on compute), and the task the
 * smoke fine-tune targets. Today the compute corpus is SYNTHETIC-BOUND: the only
 * (instruction → scene-mutation → SimContract grade) tuples in existence are
 * generated by scripts/exp3/compute_suite.py's `OPS` table. Nothing real accrues
 * because a LIVE graded compute mutation is never written to disk anywhere — the
 * CAEL audit store (state.ts) records a hash-chain over operations, NOT the
 * (instruction, contract, mutation, expected, graded-value, pass/fail) tuple a
 * trainer needs.
 *
 * This module is the compute-axis sibling of daemon-emergence-store.ts. It is a
 * TRANSPARENT persistence + grading-record layer — it changes NOTHING about how a
 * mutation is graded (the SimContract accept gate is the authority). It only:
 *   1. write-through: every graded compute mutation is appended to a durable store,
 *   2. read: a fresh on-disk read returns the accrued tuples (survives restart), and
 *   3. export: a normalized JSONL dump in the EXACT row shape harvest_real.py reads
 *      (system / user / target / grader / grader_key / family / modality / source).
 *
 * MECHANISM (why append-only JSONL, mirroring daemon-emergence-store.ts)
 * ─────────────────────────────────────────────────────────────────────
 * The native-model corpus has to accrue on the LOCAL machine, where there is no
 * DATABASE_URL. The CAEL-audit store and the emergence store both prove this
 * pattern works for graded-trace accrual without Postgres: append-only JSONL under
 * <HOLOMESH_DATA_DIR>, hydrate on read. A corpus is naturally append-only
 * (ever-growing), and harvest_real.py already reads JSONL. When DATABASE_URL IS
 * present (Railway multi-instance), we also fire-and-forget into Postgres JSONB so
 * cross-instance reads converge — additive, never the source of truth for the
 * local trainer. This is byte-for-byte the same mechanism as
 * daemon-emergence-store.ts §appendEmergenceRecord.
 *
 * RECORD GRAIN
 * ────────────
 * One JSONL line per GRADED compute mutation: the instruction, the scene + the
 * SimulationContract bounds it must respect, the agent's proposed mutation, the
 * ground-truth expected value, the graded actual value, pass/fail, and a reference
 * to the CAEL receipt that anchors the grade (when one was produced). That single
 * grain is exactly the (system, user, target) supervision pair a compute SFT row
 * needs — see exportTracesJsonl for the normalized projection.
 */

import * as fs from 'fs';
import * as path from 'path';
import * as os from 'os';
import {
  createRailwayPostgresPoolOptions,
  type RailwayPostgresPoolOptions,
} from './postgres-pool-options';

// ── Data dir (same root the rest of mcp-server persists under) ────────────────
//
// Matches daemon-emergence-store.ts / holomesh/state.ts HOLOMESH_DATA_DIR so all
// durable state lives in one place. We read the env var directly (not the symbol)
// to avoid a load cycle with the holomesh barrel.

const DATA_ROOT =
  process.env.HOLOMESH_DATA_DIR ||
  path.join(process.env.HOLOSCRIPT_CACHE_DIR || path.join(os.homedir(), '.holoscript'), 'holomesh');

const COMPUTE_DIR = path.join(DATA_ROOT, 'compute-traces');
const TRACE_PATH = path.join(COMPUTE_DIR, 'compute-traces.jsonl');

// ── Durable record shape ──────────────────────────────────────────────────────
//
// Serialization-safe (plain JSON — no Map/Set). A loss-free encoding of one graded
// compute mutation. This is NOT the export format (see buildNormalizedRows for the
// normalized training shape).

/** A single property bound declared by a SimulationContract. */
export interface ComputePropertyBound {
  /** Trait family, e.g. "glow", "rigidbody". */
  trait: string;
  /** Property key, e.g. "intensity", "mass". */
  property: string;
  /** Lower bound of the allowed range. */
  min: number;
  /** Upper bound of the allowed range. */
  max: number;
  /** The property's current value before the mutation. */
  current: number;
}

/** The mutation the agent proposed (the candidate answer being graded). */
export interface ComputeMutation {
  /** The tool the agent chose, e.g. "set_trait_property". */
  tool: string;
  /** The object the mutation targets. */
  objectName: string;
  /** Trait family being mutated. */
  traitName: string;
  /** Property key being mutated. */
  propertyKey: string;
  /** The value the agent proposed (the candidate compute answer). */
  propertyValue: number;
}

/**
 * One durable line in the compute-axis corpus: a graded compute mutation.
 * `passed` is the SimContract accept-gate verdict; `expectedValue` is the
 * ground-truth the gate graded against; `gradedValue` is the (clamped) actual
 * applied value. `caelReceiptRef` references the CAEL trace that anchors the
 * grade, when the grading site produced one (provenance; may be null for an
 * un-anchored local grade).
 */
export interface ComputeTraceRecord {
  /** Stable session/agent id that produced the trace (provenance + dedup). */
  sessionId: string;
  /** The natural-language instruction the agent was given. */
  instruction: string;
  /** The SimulationContract id the mutation must respect. */
  contractId: string;
  /** The scene source the instruction was applied to. */
  scene: string;
  /** The property bound the SimContract declared (the in-context constraint). */
  bound: ComputePropertyBound;
  /** The mutation the agent proposed. */
  mutation: ComputeMutation;
  /** Ground-truth expected value the SimContract gate grades against. */
  expectedValue: number;
  /** The graded (clamped-to-bounds) value actually applied. */
  gradedValue: number;
  /** SimContract accept-gate verdict. */
  passed: boolean;
  /** Op family label (e.g. "midpoint", "plus30") when known — corpus balance signal. */
  opLabel: string | null;
  /** Reference to the CAEL receipt anchoring this grade (provenance), if any. */
  caelReceiptRef: string | null;
  /** ISO-8601 server stamp of the grade. */
  gradedAt: string;
}

// ── Optional Postgres write-through (Railway multi-instance) ──────────────────
//
// Mirrors daemon-emergence-store.ts: when DATABASE_URL is present we ALSO push to
// a JSONB table. JSONL on disk remains the source of truth for the local trainer;
// this only helps cross-instance reads converge. Lazy + fire-and-forget so a DB
// hiccup never blocks (or changes) the grading path.

interface PgLike {
  query: (
    sql: string,
    params?: unknown[]
  ) => Promise<{ rows: Array<{ data: ComputeTraceRecord }> }>;
}

export type ComputeTracePostgresPoolOptions = RailwayPostgresPoolOptions;

export function createComputeTracePostgresPoolOptions(
  databaseUrl: string
): ComputeTracePostgresPoolOptions {
  return createRailwayPostgresPoolOptions(databaseUrl);
}

const COMPUTE_SCHEMA_SQL = `
CREATE TABLE IF NOT EXISTS compute_trace_corpus (
  id          BIGSERIAL PRIMARY KEY,
  session_id  TEXT NOT NULL,
  contract_id TEXT NOT NULL,
  passed      BOOLEAN NOT NULL,
  data        JSONB NOT NULL,
  created_at  TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);
CREATE INDEX IF NOT EXISTS idx_compute_session ON compute_trace_corpus (session_id);
CREATE INDEX IF NOT EXISTS idx_compute_created ON compute_trace_corpus (created_at);
`;

let pgPool: PgLike | null = null;
let pgReady: Promise<void> | null = null;

function maybeInitPg(): void {
  if (pgPool || pgReady) return;
  const databaseUrl = process.env.DATABASE_URL;
  if (!databaseUrl) return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { Pool } = require('pg') as {
      Pool: new (cfg: ComputeTracePostgresPoolOptions) => PgLike;
    };
    const pool = new Pool(createComputeTracePostgresPoolOptions(databaseUrl));
    pgPool = pool;
    pgReady = pool
      .query(COMPUTE_SCHEMA_SQL)
      .then(() => undefined)
      .catch((e: unknown) => {
        console.warn('[ComputeTraceStore] schema init failed:', e);
      });
    console.log('[ComputeTraceStore] PostgreSQL write-through active');
  } catch (e) {
    console.warn('[ComputeTraceStore] DATABASE_URL set but pg failed to load:', e);
  }
}

function pgAppend(record: ComputeTraceRecord): void {
  maybeInitPg();
  if (!pgPool || !pgReady) return;
  pgReady
    .then(() =>
      pgPool!.query(
        `INSERT INTO compute_trace_corpus (session_id, contract_id, passed, data) VALUES ($1, $2, $3, $4)`,
        [record.sessionId, record.contractId, record.passed, JSON.stringify(record)]
      )
    )
    .catch((e: unknown) => {
      console.warn('[ComputeTraceStore] pg append failed:', e);
    });
}

// ── Append (write-through) ────────────────────────────────────────────────────
//
// Synchronous JSONL append (matches daemon-emergence-store.ts appendEmergenceRecord
// and state.ts persistCaelAuditRecord). Never throws into the grading path — a
// persistence failure must not change the grade. Honors a dedicated test opt-out
// env, mirroring the sibling stores.

let dirEnsured = false;

function ensureDir(): void {
  if (dirEnsured) return;
  if (!fs.existsSync(COMPUTE_DIR)) {
    fs.mkdirSync(COMPUTE_DIR, { recursive: true });
  }
  dirEnsured = true;
}

/**
 * Persist one graded compute trace. Write-through: JSONL on disk (source of truth)
 * + optional Postgres (multi-instance). Best-effort — swallows errors so the live
 * grading path is never affected.
 */
export function appendComputeTrace(record: ComputeTraceRecord): void {
  if (
    process.env.NODE_ENV === 'test' &&
    process.env.HOLOMESH_TEST_DISABLE_COMPUTE_TRACE_PERSISTENCE === '1'
  ) {
    return;
  }
  try {
    ensureDir();
    fs.appendFileSync(TRACE_PATH, `${JSON.stringify(record)}\n`, 'utf8');
  } catch (err) {
    console.warn(`[ComputeTraceStore] JSONL append failed: ${(err as Error).message}`);
  }
  pgAppend(record);
}

// ── Read (hydration source) ───────────────────────────────────────────────────

/**
 * Read every persisted compute trace from disk, oldest-first (append order).
 * Corrupt / half-flushed lines are skipped (honest: rare, never fatal). Returns
 * [] when nothing has accrued yet. This is the local source of truth — Postgres
 * is only consulted by the caller when it explicitly wants cross-instance reads.
 */
export function readComputeTraces(): ComputeTraceRecord[] {
  if (!fs.existsSync(TRACE_PATH)) return [];
  const out: ComputeTraceRecord[] = [];
  const lines = fs.readFileSync(TRACE_PATH, 'utf8').split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      out.push(JSON.parse(trimmed) as ComputeTraceRecord);
    } catch {
      // Corrupt line — skip.
    }
  }
  return out;
}

/**
 * Read every persisted compute trace from Postgres, oldest-first (created_at).
 * Unpacks the JSONB `data` column. Returns [] when Postgres is not configured,
 * the table is empty, or the query fails (honest: never fatal). This is the
 * Railway-durable counterpart to the JSONL reader — the local file is wiped on
 * every redeploy; Postgres survives (HoloTune Phase 0b BugB — D.086).
 */
export async function readComputeTracesFromPg(): Promise<ComputeTraceRecord[]> {
  maybeInitPg();
  if (!pgPool || !pgReady) return [];
  try {
    await pgReady;
    const result = await pgPool.query(
      'SELECT data FROM compute_trace_corpus ORDER BY created_at ASC'
    );
    return result.rows.map((row) => row.data);
  } catch (e) {
    console.warn('[ComputeTraceStore] pg read failed:', e);
    return [];
  }
}

/**
 * Resolve the durable compute corpus, Railway-safe: prefer the local JSONL
 * (source of truth on dev / single-instance); when it is absent/empty AND
 * DATABASE_URL is configured, fall back to the Postgres corpus. Use this instead
 * of the sync readComputeTraces() wherever a complete cross-instance read is
 * required (e.g. the export path on a deployed, ephemeral-FS instance).
 */
export async function readComputeTracesResolved(): Promise<ComputeTraceRecord[]> {
  const local = readComputeTraces();
  if (local.length > 0) return local;
  if (process.env.DATABASE_URL) return readComputeTracesFromPg();
  return local;
}

// ── Export (normalized training corpus) ───────────────────────────────────────
//
// The corpus consumer (ai-ecosystem/scripts/corpus/harvest_real.py) normalizes
// every source into (system, user, target, grader, grader_key, family, modality,
// source). The compute source is the EXACT-VALUE task, so we emit that exact shape.
// CRITICAL: we reuse the SAME compute SYSTEM/user framing as
// scripts/exp3/compute_suite.py so a row harvested from a LIVE grade is
// contract-identical (by construction) to a synthetic train/eval row — the
// property EXP-3's compute_suite.py was built to guarantee (train/eval no-drift).
//
//   system   : the compute-task framing (single scene mutation, respect bounds,
//              JSON-only) — identical to compute_suite.SYSTEM.
//   user     : instruction + scene + declared bounds (the model's input) — built
//              by the same template compute_suite._row uses.
//   target   : the JSON mutation with the EXPECTED value (the supervision signal).
//   grader   : { kind: "simcontract_compute", expected, passed, ... }.
//   grader_key: ["compute", sessionId, seq] — provenance + dedup handle.

export interface NormalizedComputeRow {
  system: string;
  user: string;
  target: string;
  grader: {
    kind: string;
    expected: number;
    graded: number;
    passed: boolean;
    contractId: string;
    caelReceiptRef: string | null;
  };
  grader_key: [string, string, number];
  family: string;
  modality: string;
  source: string;
  session: string;
  op_label: string | null;
  timestamp: string;
}

/**
 * The compute-task SYSTEM prompt — kept byte-identical to
 * scripts/exp3/compute_suite.py SYSTEM so a harvested live row trains the exact
 * same task as the synthetic corpus (no train/serve framing drift, the W.667
 * lesson).
 */
export const SYSTEM_COMPUTE =
  "You are editing a HoloScript scene. Apply the user's instruction as a SINGLE " +
  "scene mutation that RESPECTS the scene's declared SimulationContract — honor " +
  'property bounds. Respond with ONLY a JSON object and no prose: ' +
  '{"tool":"<tool>","input":{...}}. Property mutations use input ' +
  '{object_name, trait_name, property_key, property_value}.';

/** Round to 2 decimals — matches compute_suite.r2 so values align with synthetic. */
function r2(x: number): number {
  return Math.round(x * 100) / 100;
}

/**
 * Build the `user` text for a compute row in the EXACT shape compute_suite._row
 * emits, from a durable record. Keeping this projection identical to the Python
 * generator is what makes a live-harvested row indistinguishable (to the trainer)
 * from a synthetic one.
 */
function buildUser(rec: ComputeTraceRecord): string {
  return rec.instruction;
}

/**
 * Build the `target` JSON in the EXACT shape compute_suite.gen_train emits, but
 * carrying the EXPECTED (ground-truth) value — the supervision signal. We emit the
 * `set_trait_property` tool shape regardless of which tool the agent actually
 * proposed, because the TARGET is the correct answer, not the agent's attempt.
 */
function buildTarget(rec: ComputeTraceRecord): string {
  return JSON.stringify({
    tool: 'set_trait_property',
    input: {
      trait_name: rec.mutation.traitName,
      property_key: rec.mutation.propertyKey,
      property_value: r2(rec.expectedValue),
    },
  });
}

/**
 * Project the persisted compute corpus into normalized training rows. Pure over
 * the on-disk records (no I/O beyond the read) so it is trivially testable.
 * `records` defaults to the on-disk corpus.
 */
export function buildNormalizedRows(
  records: ComputeTraceRecord[] = readComputeTraces()
): NormalizedComputeRow[] {
  const rows: NormalizedComputeRow[] = [];
  const seqBySession = new Map<string, number>();

  for (const rec of records) {
    const seq = (seqBySession.get(rec.sessionId) ?? -1) + 1;
    seqBySession.set(rec.sessionId, seq);

    rows.push({
      system: SYSTEM_COMPUTE,
      user: buildUser(rec),
      target: buildTarget(rec),
      grader: {
        kind: 'simcontract_compute',
        expected: r2(rec.expectedValue),
        graded: r2(rec.gradedValue),
        passed: rec.passed,
        contractId: rec.contractId,
        caelReceiptRef: rec.caelReceiptRef,
      },
      grader_key: ['compute', rec.sessionId, seq],
      family: 'real_compute',
      modality: 'compute',
      source: 'compute-trace-corpus',
      session: rec.sessionId,
      op_label: rec.opLabel,
      timestamp: rec.gradedAt,
    });
  }
  return rows;
}

/**
 * Export the persisted compute corpus to a normalized JSONL file the corpus
 * builder (harvest_real.py) can consume. Returns the path written and the row
 * count. If `outPath` is omitted, writes alongside the raw corpus as
 * `compute-corpus.normalized.jsonl`. A `passedOnly` filter keeps only
 * positive-supervision rows (the SimContract PASSED) — the default for SFT, since
 * a failed compute mutation is a negative example, not a target.
 */
export function exportTracesJsonl(
  outPath?: string,
  opts: { passedOnly?: boolean } = {}
): { path: string; rows: number; bytes: number } {
  let rows = buildNormalizedRows();
  if (opts.passedOnly) {
    rows = rows.filter((r) => r.grader.passed);
  }
  const target = outPath || path.join(COMPUTE_DIR, 'compute-corpus.normalized.jsonl');
  ensureDir();
  const body = rows.map((r) => JSON.stringify(r)).join('\n') + (rows.length ? '\n' : '');
  fs.writeFileSync(target, body, 'utf8');
  return { path: target, rows: rows.length, bytes: Buffer.byteLength(body, 'utf8') };
}

/**
 * Railway-safe export: identical to exportTracesJsonl, but resolves the corpus via
 * readComputeTracesResolved() so a deployed (ephemeral-FS) instance reads the
 * durable Postgres corpus when the local JSONL has been wiped (HoloTune Phase 0b
 * BugB). Prefer this in the deployed export path; the sync exportTracesJsonl
 * remains for local/dev where the JSONL is the source of truth.
 */
export async function exportTracesJsonlResolved(
  outPath?: string,
  opts: { passedOnly?: boolean } = {}
): Promise<{ path: string; rows: number; bytes: number }> {
  const records = await readComputeTracesResolved();
  let rows = buildNormalizedRows(records);
  if (opts.passedOnly) {
    rows = rows.filter((r) => r.grader.passed);
  }
  const target = outPath || path.join(COMPUTE_DIR, 'compute-corpus.normalized.jsonl');
  ensureDir();
  const body = rows.map((r) => JSON.stringify(r)).join('\n') + (rows.length ? '\n' : '');
  fs.writeFileSync(target, body, 'utf8');
  return { path: target, rows: rows.length, bytes: Buffer.byteLength(body, 'utf8') };
}

export type ComputeTraceStats = {
  tracePath: string;
  exists: boolean;
  records: number;
  passed: number;
  failed: number;
  sessions: number;
  contracts: number;
  source: 'local-jsonl' | 'postgres-fallback' | 'empty';
};

function summarizeComputeTraces(
  records: ComputeTraceRecord[],
  source: ComputeTraceStats['source']
): ComputeTraceStats {
  const sessions = new Set(records.map((r) => r.sessionId));
  const contracts = new Set(records.map((r) => r.contractId));
  let passed = 0;
  for (const r of records) if (r.passed) passed++;
  return {
    tracePath: TRACE_PATH,
    exists: fs.existsSync(TRACE_PATH),
    records: records.length,
    passed,
    failed: records.length - passed,
    sessions: sessions.size,
    contracts: contracts.size,
    source,
  };
}

/** Diagnostics: where the corpus lives + how much real compute data has accrued. */
export function computeTraceStats(): ComputeTraceStats {
  const records = readComputeTraces();
  return summarizeComputeTraces(records, records.length > 0 ? 'local-jsonl' : 'empty');
}

/**
 * Railway-safe diagnostics: summarize the same corpus source that resolved
 * export would use. This keeps `holo_export_compute_traces` from reporting
 * Postgres-exported rows with local-only stats.records = 0 on ephemeral disks.
 */
export async function computeTraceStatsResolved(): Promise<ComputeTraceStats> {
  const local = readComputeTraces();
  if (local.length > 0) {
    return summarizeComputeTraces(local, 'local-jsonl');
  }
  if (process.env.DATABASE_URL) {
    const pgRecords = await readComputeTracesFromPg();
    if (pgRecords.length > 0) {
      return summarizeComputeTraces(pgRecords, 'postgres-fallback');
    }
  }
  return summarizeComputeTraces(local, 'empty');
}

// ── Grading primitive (the live counterpart of compute_suite's accept gate) ────
//
// This is the SINGLE call a live grading site invokes to (a) grade a proposed
// compute mutation against a SimulationContract bound and (b) write the graded
// tuple through to the durable corpus. It is ADDITIVE: there is no existing live
// `set_trait_property` grading site in mcp-server to modify (verified by grep), so
// this introduces no change to existing grading semantics. The accept-gate logic
// is byte-equivalent to compute_suite._row's grade:
//
//     expected = r2(clamp(min, max, expectedRaw))
//     passed   = (r2(proposed) === expected)         # exact-value match
//     graded   = clamp(min, max, proposed)           # what would actually apply
//
// The caller supplies the GROUND-TRUTH expected value (it knows the op family it
// asked for); this function does NOT recompute the op — it is the grader+recorder,
// not the task generator. That keeps grading-semantics ownership with the contract,
// exactly as the constraint requires.

export interface GradeComputeInput {
  sessionId: string;
  instruction: string;
  contractId: string;
  scene: string;
  bound: ComputePropertyBound;
  mutation: ComputeMutation;
  /** Ground-truth expected value (the correct answer for the asked op). */
  expectedValue: number;
  /** Op family label, if the caller knows it (corpus-balance signal). */
  opLabel?: string | null;
  /** CAEL receipt reference anchoring this grade, if one was produced. */
  caelReceiptRef?: string | null;
}

export interface GradeComputeResult {
  passed: boolean;
  expectedValue: number;
  gradedValue: number;
  record: ComputeTraceRecord;
}

function clamp(min: number, max: number, v: number): number {
  return Math.max(min, Math.min(max, v));
}

/**
 * Grade a proposed compute mutation against its SimulationContract bound AND
 * write the resulting (instruction → mutation → expected/graded → pass/fail →
 * CAEL ref) tuple through to the durable corpus. Returns the verdict + the
 * persisted record.
 *
 * Pure grading semantics (exact-value match within 2dp, clamp-to-bounds), then a
 * best-effort write-through. The write-through never affects the returned verdict.
 */
export function gradeComputeMutation(input: GradeComputeInput): GradeComputeResult {
  const { bound, mutation, expectedValue } = input;
  const expected = r2(clamp(bound.min, bound.max, expectedValue));
  const graded = clamp(bound.min, bound.max, mutation.propertyValue);
  const passed = r2(mutation.propertyValue) === expected;

  const record: ComputeTraceRecord = {
    sessionId: input.sessionId,
    instruction: input.instruction,
    contractId: input.contractId,
    scene: input.scene,
    bound: input.bound,
    mutation: input.mutation,
    expectedValue: expected,
    gradedValue: graded,
    passed,
    opLabel: input.opLabel ?? null,
    caelReceiptRef: input.caelReceiptRef ?? null,
    gradedAt: new Date().toISOString(),
  };

  // Write-through (additive): mirror the graded tuple to the durable corpus.
  // Best-effort; never affects the verdict returned above.
  appendComputeTrace(record);

  return { passed, expectedValue: expected, gradedValue: graded, record };
}

/** Test-only: the resolved corpus path (so tests can point HOLOMESH_DATA_DIR). */
export function _tracePathForTest(): string {
  return TRACE_PATH;
}

/** Test-only: inject a fake Postgres pool without loading pg or opening sockets. */
export function _setPgForTest(pool: PgLike | null, ready: Promise<void> | null = null): void {
  pgPool = pool;
  pgReady = ready;
}
